import { ReactNode } from 'react';
import {
  IconAlertTriangle,
  IconAlertTriangleFilled,
  IconBan,
  IconCheck,
  IconCircleCheck,
  IconCircleDot,
  IconCircleX,
  IconClock,
  IconFile,
  IconFileAlert,
  IconFileCheck,
  IconFileX,
  IconInfoCircle,
  IconLoader,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import type { BadgeColor, BadgeVariant } from '../Badge';

export type EntityType =
  | 'import_mapping'
  | 'file'
  | 'upload'
  | 'wizard_step'
  | 'file_mapping'
  | 'import'
  | 'csv_file';

export type StatusConfig = {
  color?: BadgeColor;
  variant?: BadgeVariant;
  icon?: () => ReactNode;
  label?: string;
};

// Default status mapping - applies to all entity types unless overridden
export const defaultStatusMapping: Record<string, StatusConfig> = {
  // Common statuses
  pending: {
    color: 'default',
    variant: 'dashed',
    icon: () => <IconClock />,
    label: 'Pending',
  },
  processing: {
    color: 'info',
    variant: 'soft',
    icon: () => <IconLoader />,
    label: 'Processing',
  },
  completed: {
    color: 'success',
    variant: 'surface',
    icon: () => <IconCircleCheck />,
    label: 'Completed',
  },
  failed: {
    color: 'error',
    variant: 'surface',
    icon: () => <IconCircleX />,
    label: 'Failed',
  },
  error: {
    color: 'error',
    variant: 'surface',
    icon: () => <IconAlertTriangle />,
    label: 'Error',
  },
  cancelled: {
    color: 'error',
    variant: 'surface',
    icon: () => <IconBan />,
    label: 'Cancelled',
  },
  current: {
    color: 'info',
    variant: 'surface',
    icon: () => <IconCircleDot />,
    label: 'Current',
  },
  active: {
    color: 'success',
    variant: 'surface',
    icon: () => <IconCheck />,
    label: 'Active',
  },
  inactive: {
    color: 'default',
    variant: 'surface',
    icon: () => <IconX />,
    label: 'Inactive',
  },
  // Import mapping specific
  created: {
    color: 'gray',
    variant: 'soft',
    icon: () => <IconFile />,
    label: 'Created',
  },
  valid_for_import: {
    color: 'success',
    variant: 'soft',
    icon: () => <IconCircleCheck />,
    label: 'Valid for Import',
  },
  invalid_for_import: {
    color: 'error',
    variant: 'soft',
    icon: () => <IconCircleX />,
    label: 'Invalid for Import',
  },
  imported: {
    color: 'success',
    variant: 'solid',
    icon: () => <IconCheck />,
    label: 'Imported',
  },
  // File specific
  'ready-to-check': {
    color: 'info',
    variant: 'soft',
    icon: () => <IconFileCheck />,
    label: 'Ready to Check',
  },
  duplicated: {
    color: 'warning',
    variant: 'dashed',
    icon: () => <IconAlertTriangleFilled />,
    label: 'Duplicated',
  },
  allowed: {
    color: 'success',
    variant: 'outlined',
    icon: () => <IconCheck />,
    label: 'Allowed',
  },
  'not-allowed': {
    color: 'error',
    variant: 'dashed',
    icon: () => <IconX />,
    label: 'Not Allowed',
  },
  exists: {
    color: 'warning',
    variant: 'soft',
    icon: () => <IconInfoCircle />,
    label: 'Exists',
  },
  uploading: {
    color: 'info',
    variant: 'soft',
    icon: () => <IconUpload />,
    label: 'Uploading',
  },
  uploaded: {
    color: 'success',
    variant: 'surface',
    icon: () => <IconCircleCheck />,
    label: 'Uploaded',
  },
  // File mapping specific
  excellent: {
    color: 'success',
    variant: 'surface',
    icon: () => <IconCircleCheck />,
    label: 'Ready to Import!',
  },
  good: {
    color: 'info',
    variant: 'soft',
    icon: () => <IconCheck />,
    label: 'Good Match',
  },
  partial: {
    color: 'warning',
    variant: 'soft',
    icon: () => <IconAlertTriangle />,
    label: 'Partial Match',
  },
  poor: {
    color: 'error',
    variant: 'soft',
    icon: () => <IconX />,
    label: 'Poor Match',
  },
  'missing-required': {
    color: 'error',
    variant: 'soft',
    icon: () => <IconFileX />,
    label: 'Missing Required Fields',
  },
  'missing-required-metadata': {
    color: 'error',
    variant: 'soft',
    icon: () => <IconFileAlert />,
    label: 'Missing Required Metadata',
  },
  unmapped: {
    color: 'gray',
    variant: 'dashed',
    icon: () => <IconFile />,
    label: 'Unmapped',
  },
  // CSV file validation
  valid: {
    color: 'success',
    variant: 'outlined',
    icon: () => <IconCheck />,
    label: 'Valid',
  },
  invalid: {
    color: 'error',
    variant: 'dashed',
    icon: () => <IconX />,
    label: 'Invalid',
  },
};

// Entity type specific overrides
export const entityTypeOverrides: Partial<
  Record<EntityType, Record<string, Partial<StatusConfig>>>
> = {
  import_mapping: {
    created: {
      color: 'info',
      variant: 'soft',
    },
    valid_for_import: {
      color: 'success',
      variant: 'soft',
    },
    imported: {
      color: 'success',
      variant: 'solid',
    },
  },
  file: {
    'ready-to-check': {
      color: 'info',
      variant: 'dashed',
      icon: () => <IconFileCheck />,
      label: 'Ready to Check',
    },
    duplicated: {
      color: 'warning',
      variant: 'dashed',
      icon: () => <IconAlertTriangleFilled />,
      label: 'Duplicated',
    },
    allowed: {
      color: 'success',
      variant: 'outlined',
      icon: () => <IconCheck />,
      label: 'Allowed',
    },
    'not-allowed': {
      color: 'error',
      variant: 'dashed',
      icon: () => <IconX />,
      label: 'Not Allowed',
    },
    exists: {
      color: 'warning',
      variant: 'soft',
      icon: () => <IconInfoCircle />,
      label: 'Exists',
    },
  },
  upload: {
    pending: {
      color: 'default',
      variant: 'dashed',
      icon: () => <IconClock />,
      label: 'Pending',
    },
    uploading: {
      color: 'info',
      variant: 'soft',
      icon: () => <IconLoader className="animate-spin" />,
      label: 'Uploading',
    },
    uploaded: {
      color: 'success',
      variant: 'surface',
      icon: () => <IconCircleCheck />,
      label: 'Uploaded',
    },
    failed: {
      color: 'error',
      variant: 'surface',
      icon: () => <IconCircleX />,
      label: 'Failed',
    },
  },
  wizard_step: {
    current: {
      color: 'primary',
      variant: 'surface',
    },
    processing: {
      color: 'info',
      variant: 'soft',
      icon: () => <IconLoader className="animate-spin" />,
    },
  },
  file_mapping: {
    excellent: {
      color: 'success',
      variant: 'surface',
    },
  },
  csv_file: {
    valid: {
      color: 'success',
      variant: 'outlined',
      icon: () => <IconCheck />,
      label: 'Valid',
    },
    invalid: {
      color: 'error',
      variant: 'dashed',
      icon: () => <IconX />,
      label: 'Invalid',
    },
    pending: {
      color: 'default',
      variant: 'dashed',
      icon: () => <IconClock />,
      label: 'Pending',
    },
    approved: {
      color: 'success',
      variant: 'ghost',
      icon: () => <IconCheck />,
      label: 'Approved',
    },
    rejected: {
      color: 'error',
      variant: 'ghost',
      icon: () => <IconX />,
      label: 'Rejected',
    },
  },
};

/**
 * Get status configuration with priority:
 * 1. Entity type override (if exists)
 * 2. Default status mapping
 */
export function getStatusConfig(
  status: string,
  entityType?: EntityType
): StatusConfig {
  // Check entity type override first
  if (entityType && entityTypeOverrides[entityType]?.[status]) {
    const override = entityTypeOverrides[entityType]![status];
    const defaultConfig = defaultStatusMapping[status] || {};
    return {
      ...defaultConfig,
      ...override,
    };
  }

  // Fall back to default mapping
  return defaultStatusMapping[status] || {};
}
